import {Component, OnInit} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {CONTRACTS_LIST_ROUTE, contractDetailRoute} from '@shared/services/router/contract-routes';
import {ContractFormComponent, ContractFormValue} from '../form/contract-form.component';

/** The API answers a duplicate code with this identifier (422). */
const DUPLICATE_CODE = 'contract_code_already_exists';

/**
 * UNIVAPLAT-3516: New contract page (route `cdss/admin/contracts/new`, CREATE_CONTRACT).
 * The backend owns the error text; nothing from the form or the response is logged.
 */
@Component({
    selector: 'cdss-contract-new-page',
    standalone: true,
    imports: [RouterLink, ContractFormComponent],
    templateUrl: './contract-new.page.html',
    styleUrls: ['./contract-new.page.scss']
})
export class ContractNewPage implements OnInit {
    readonly listRoute = '/' + CONTRACTS_LIST_ROUTE;

    saving = false;
    codeError: string | null = null;

    constructor(
        private breadcrumbService: BreadcrumbService,
        private contractsService: ContractsService,
        private toastr: ToastrService,
        private router: Router,
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: 'Contracts', url: this.listRoute},
            {title: 'New contract', url: null as unknown as string},
        ]);
    }

    async onSave(value: ContractFormValue): Promise<void> {
        if (this.saving) {
            return;
        }
        this.saving = true;
        this.codeError = null;
        try {
            const created = await this.contractsService.create(value);
            this.toastr.success('Contract created', 'Success');
            await this.router.navigateByUrl('/' + contractDetailRoute(created.id));
        } catch (error: any) {
            const message: string | undefined = error?.error?.error;
            if (this.isDuplicateCode(error)) {
                this.codeError = message || 'This contract code already exists.';
            } else {
                if (!isAlreadyNotified(error)) {
                    this.toastr.error(message || 'Something went wrong. Please try again.');
                }
            }
        } finally {
            this.saving = false;
        }
    }

    private isDuplicateCode(error: any): boolean {
        const body = error?.error;
        return error?.status === 422 && [body?.code, body?.error, body?.message, body?.detail]
            .some(field => typeof field === 'string' && field.includes(DUPLICATE_CODE));
    }
}
